import React, { useState, useContext } from "react";
import { Helmet } from 'react-helmet-async';
import { Typography, Box, Grid, TextField, Button, Paper, Divider, Link, Container } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { AuthContext } from "../components/Auth";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const { firestoreInstance } = useContext(AuthContext);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!firestoreInstance) {
      setError("System error: database not available.");
      return;
    }
    try {
      await addDoc(collection(firestoreInstance, "website_contact"), {
        name: form.name,
        email: form.email,
        message: form.message,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      setError("Failed to send message. Please try again later.");
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Helmet>
        <title>Contact — MonadX</title>
        <meta name="description" content="Contact MonadX — send feedback, questions, or support requests." />
        <meta property="og:title" content="Contact — MonadX" />
      </Helmet>
      <Typography variant="h4" gutterBottom>Contact Us</Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        We'd love to hear from you! Fill out the form or reach us directly.
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
            {submitted ? (
              <Typography color="primary" variant="h6">Thank you for your message!</Typography>
            ) : (
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  label="Message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                  multiline
                  minRows={4}
                />
                {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
                <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }} fullWidth>
                  Send Message
                </Button>
              </form>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>Contact Information</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ mr: 1 }} color="primary" />
              <Typography variant="body2">info@monadx.me</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Follow Us</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Link href="#" color="inherit"><FacebookIcon color="primary" /></Link>
              <Link href="#" color="inherit"><TwitterIcon color="primary" /></Link>
              <Link href="#" color="inherit"><LinkedInIcon color="primary" /></Link>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactPage;